import React from "react";
import { DayPicker } from "react-day-picker";
import { format } from "date-fns";
import { ArrowRight } from "lucide-react";
import { Drawer, DrawerContent, DrawerTitle } from "@/components/ui/drawer";
import { RatingBadge } from "./RatingBadge";
import { GuestSteppers } from "./GuestSteppers";
import { CouponField } from "./CouponField";
import { TrustBadges } from "./TrustBadges";
import { fmtAmount } from "@/lib/stayflowApi";

export const ReserveSheet = ({
  open, onOpenChange, room, price, nights, range, setRange, today, windowEnd, busyDates, availLoading,
  adults, setAdults, childrenCount, setChildrenCount, maxAdults = 1, maxChildren = 0, childRuleText = "", childrenAllowed = true,
  couponProps, total, onContinue,
}) => (
  <Drawer open={open} onOpenChange={onOpenChange}>
    <DrawerContent className="rounded-t-3xl max-h-[94vh]" data-testid="reserve-sheet">
      <DrawerTitle className="sr-only">Complete your booking</DrawerTitle>

      <div className="px-5 pb-3 pt-1 border-b border-black/5 flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="font-heading font-bold text-ink truncate">{room?.name}</p>
          <p className="text-xs text-ink-soft mt-0.5">{fmtAmount(price)} / night incl. GST</p>
        </div>
        <RatingBadge source={room || {}} testid="sheet-rating-badge" className="shrink-0" />
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto px-5 py-4 space-y-6">
        <section>
          <h3 className="text-sm font-heading font-bold text-ink mb-2">Your dates</h3>
          <div className="rdp-wrap flex justify-center rounded-2xl border border-black/10 py-2" style={{ "--rdp-cell-size": "42px" }}>
            <DayPicker
              mode="range" selected={range} onSelect={setRange}
              defaultMonth={range?.from || today} numberOfMonths={1}
              fromDate={today} toDate={windowEnd}
              disabled={[{ before: today }, ...busyDates]}
              data-testid="sheet-date-picker"
            />
          </div>
          <p className="mt-1.5 text-xs text-ink-faint">{availLoading ? "Loading availability…" : "Greyed-out dates are already booked."}</p>
          {range?.from && range?.to && (
            <p className="mt-1 text-sm text-ink font-medium" data-testid="sheet-selected-dates">
              {format(range.from, "dd MMM")} → {format(range.to, "dd MMM yyyy")} · {nights} night{nights !== 1 ? "s" : ""}
            </p>
          )}
        </section>

        <section>
          <h3 className="text-sm font-heading font-bold text-ink mb-1">Guests</h3>
          <div className="rounded-2xl border border-black/10 px-4">
            <GuestSteppers adults={adults} setAdults={setAdults} childrenCount={childrenCount} setChildrenCount={setChildrenCount} maxAdults={maxAdults} maxChildren={maxChildren} childRuleText={childRuleText} childrenAllowed={childrenAllowed} idPrefix="sheet-" />
          </div>
        </section>

        <section>
          <CouponField {...couponProps} idPrefix="sheet-" />
        </section>
      </div>

      <div className="border-t border-black/10 bg-white px-5 pt-3 pb-[calc(0.9rem+env(safe-area-inset-bottom))]">
        <div className="flex items-center justify-between gap-4">
          <div className="min-w-0">
            {nights > 0 ? (
              <>
                <p className="font-heading font-bold text-ink" data-testid="sheet-footer-total">{total != null ? fmtAmount(total) : "Checking…"}</p>
                <p className="text-xs text-ink-soft">{nights} night{nights !== 1 ? "s" : ""} · incl. GST</p>
              </>
            ) : (
              <p className="text-sm text-ink-soft">Select your dates to see the total</p>
            )}
          </div>
          <button onClick={onContinue} disabled={nights <= 0} data-testid="sheet-book-pay-btn" className="shrink-0 inline-flex items-center justify-center gap-2 rounded-full bg-brand text-white font-semibold h-12 px-7 disabled:opacity-60 transition-transform hover:-translate-y-0.5 disabled:hover:translate-y-0">
            Continue <ArrowRight className="w-4 h-4" />
          </button>
        </div>
        <p className="mt-1.5 text-center text-[11px] text-ink-faint">Next: review your stay and pay securely — no sign-up needed.</p>
        <TrustBadges className="mt-2" />
      </div>
    </DrawerContent>
  </Drawer>
);

export default ReserveSheet;
